import { useEffect, useContext, useMemo } from 'react'
import { withRouter } from 'react-router-dom'
import { ContentHeader } from 'components/shared/content'
import * as H from 'history'
import { TemperatureLedgerMaster, TemperatureLedgerSearchForm } from 'entities/temperature_ledger'
import { useFetchTemperatureLedgersApi } from 'api/temperature_ledger'
import { GlobalStateContext } from 'contexts/global_state_context'
import { useForm, useQuery } from 'utils/hooks'
import { EditTemperatureLedgerForm, NewTemperatureLedgerForm } from './temperature_ledger_form'
import { useState } from 'react'
import { SelectField } from 'components/shared/input'
import moment from 'moment'
import { A4 } from 'components/shared/print'
import { Button } from 'react-bootstrap'
import { Flex } from 'components/shared/flex'
import { DayOfWeekStrJP, getDayOfWeekJp, selectYears, setPageSize } from 'utils/util'
import { isHoliday } from '@holiday-jp/holiday_jp'
import { CustomTd } from 'components/shared/widget'
import { LedgerHeader } from 'components/ledger/ledger_componet'

type Props = {
    history: H.History
}

const TemperatureLedgerPage = (props: Props) => {
    const query = useQuery<{ equipmentId?: string }>()
    const searchForm = useForm<TemperatureLedgerSearchForm>({
        year: moment().format('YYYY'),
        month: moment().format('MM'),
        equipmentId: Number(query.equipmentId),
    })
    const listApi = useFetchTemperatureLedgersApi(searchForm)
    const globalState = useContext(GlobalStateContext)
    const [showEdit, setShowEdit] = useState<boolean>(false)

    useEffect(() => {
        listApi.execute()
        setPageSize('portrait')
    }, [])

    const temperatureLedgerThisMonth = useMemo(() => {
        return listApi.response.results[0]
    }, [listApi.loading, searchForm.object])

    return (
        <>
            {temperatureLedgerThisMonth && (
                <EditTemperatureLedgerForm
                    show={showEdit}
                    temperatureLedger={temperatureLedgerThisMonth}
                    onClose={() => setShowEdit(false)}
                    onComplete={() => {
                        setShowEdit(false)
                        listApi.execute()
                    }}
                />
            )}
            <ContentHeader
                className="no-print"
                headerTitle="温度設備管理台帳"
                left={
                    <>
                        {!temperatureLedgerThisMonth && <NewTemperatureLedgerForm onComplete={() => listApi.execute()} temperatureLedgerForm={searchForm} />}
                        {temperatureLedgerThisMonth && <Button onClick={() => setShowEdit(true)}>当月の台帳を編集</Button>}
                        <Button
                            onClick={() => {
                                window.print()
                            }}
                        >
                            印刷プレビュー
                        </Button>
                    </>
                }
                right={
                    <Flex style={{ marginTop: 0 }}>
                        <Flex alignItems="center">
                            <SelectField className="" form={searchForm} attr="year" selectItems={selectYears()} style={{ width: 120 }} />
                            <SelectField
                                className=""
                                form={searchForm}
                                attr="month"
                                selectItems={new Array(12).fill(0).map((y, idx) => {
                                    const month = idx + 1
                                    return { label: month + '月', value: ('0' + String(month)).slice(-2) }
                                })}
                                style={{ width: 100 }}
                            />
                        </Flex>
                        <Flex alignItems="center">
                            <Button
                                variant="secondary"
                                style={{ width: 120 }}
                                onClick={() => {
                                    props.history.push(`/equipments?page=1&perPage=50`)
                                }}
                            >
                                機器一覧へ
                            </Button>
                        </Flex>
                    </Flex>
                }
            />
            {listApi.isSuccess() && <MonthlyTemperatureLedgerPaper temperatureLedger={temperatureLedgerThisMonth} />}
        </>
    )
}

export default withRouter(TemperatureLedgerPage)

type MonthlyTemperatureLedgerPaperProps = {
    temperatureLedger?: TemperatureLedgerMaster
}

const dayColor = (year: number, month: number, day: number) => {
    const date = new Date(year, month - 1, day)
    const dayOfWeek: DayOfWeekStrJP = getDayOfWeekJp(date)
    if (dayOfWeek === '土') {
        return 'rgb(107 125 235 / 47%)'
    } else if (dayOfWeek === '日' || isHoliday(date)) {
        return 'rgb(233 100 119 / 47%)'
    } else {
        return '#fff'
    }
}

const MonthlyTemperatureLedgerPaper = (props: MonthlyTemperatureLedgerPaperProps) => {
    const measureHumidity = props.temperatureLedger?.controlHumidityMax !== null || props.temperatureLedger?.controlHumidityMin !== null
    const controlHumidity = measureHumidity
        ? `${((props.temperatureLedger?.controlHumidityMax ?? 0) + (props.temperatureLedger?.controlHumidityMin ?? 0)) / 2}±${
              ((props.temperatureLedger?.controlHumidityMax ?? 0) - (props.temperatureLedger?.controlHumidityMin ?? 0)) / 2
          }%`
        : '--'
    return (
        <>
            {props.temperatureLedger ? (
                <A4 key={'key'} padding={0} landscape={false} style={{ padding: 0 }}>
                    <div className={'preview-result'} style={{ width: '100%', height: '100%', padding: 20 }}>
                        <LedgerHeader
                            headerTitle={
                                props.temperatureLedger.equipment?.equipmentCategory?.name === '検査室温湿度'
                                    ? '検査室温湿度管理台帳'
                                    : '冷蔵冷凍庫温度管理台帳'
                            }
                            recordTypeNum={props.temperatureLedger.equipment?.equipmentCategory?.code}
                            originalDocument="LSOP-QUA004"
                            versionNum={props.temperatureLedger.equipment?.equipmentCategory?.name === '検査室温湿度' ? '6' : '5'}
                            timestamp={moment().format('YYYYMMDDHHmm')}
                        ></LedgerHeader>

                        <Flex justifyContent="space-between">
                            <div style={{ fontSize: 12 }}>
                                <div>{`${props.temperatureLedger?.year}年 ${Number(props.temperatureLedger?.month)}月分`}</div>
                                <div>
                                    {`設備名/型番: ${props.temperatureLedger?.equipment?.name ?? '--'}/${
                                        props.temperatureLedger?.equipment?.number ?? '--'
                                    } (衛生${props.temperatureLedger.equipment?.equipmentAlias})`}
                                </div>
                                <div>
                                    {`
                            管理温度：${((props.temperatureLedger.controlTemperatureMax ?? 0) + (props.temperatureLedger.controlTemperatureMin ?? 0)) / 2}±${
                                        ((props.temperatureLedger.controlTemperatureMax ?? 0) - (props.temperatureLedger.controlTemperatureMin ?? 0)) / 2
                                    }℃、
                            管理湿度：${controlHumidity}
                        `}
                                </div>
                            </div>
                        </Flex>
                        <div style={{ position: 'absolute', top: 240, left: 20, width: '100%', fontSize: 10 }}>
                            <table style={{ borderCollapse: 'collapse', border: 'solid 2px', width: '94.5%' }}>
                                <tr>
                                    <th style={{ border: 'solid 1px', padding: 3, textAlign: 'center' }} rowSpan={2}>
                                        日付
                                    </th>
                                    <th style={{ width: 70, border: 'solid 1px', padding: 3, textAlign: 'center' }} rowSpan={2}>
                                        測定開始・終了時間
                                    </th>
                                    <th style={{ border: 'solid 1px', padding: 3, textAlign: 'center' }} colSpan={3}>
                                        温度℃
                                    </th>
                                    {!props.temperatureLedger?.equipment?.ondotoriSerialNumber ||
                                        (props.temperatureLedger?.equipment?.ondotoriSeries === 'TR7' && (
                                            <>
                                                <th style={{ border: 'solid 1px', padding: 3, textAlign: 'center' }} colSpan={3}>
                                                    湿度%
                                                </th>
                                            </>
                                        ))}
                                    <th style={{ border: 'solid 1px', padding: 3, textAlign: 'center', width: 100 }} rowSpan={2}>
                                        点検対応者
                                    </th>
                                    <th style={{ border: 'solid 1px', padding: 3, textAlign: 'center', width: 235 }} rowSpan={2}>
                                        特記事項
                                    </th>
                                </tr>
                                <tr>
                                    <th style={{ border: 'solid 1px', padding: 3, textAlign: 'center' }} rowSpan={2}>
                                        測定温度
                                    </th>
                                    <th style={{ border: 'solid 1px', padding: 3, textAlign: 'center' }} rowSpan={2}>
                                        最高温度
                                    </th>
                                    <th style={{ border: 'solid 1px', padding: 3, textAlign: 'center' }} rowSpan={2}>
                                        最低温度
                                    </th>

                                    {!props.temperatureLedger?.equipment?.ondotoriSerialNumber ||
                                        (props.temperatureLedger?.equipment?.ondotoriSeries === 'TR7' && (
                                            <>
                                                <th style={{ border: 'solid 1px', padding: 3, textAlign: 'center' }} rowSpan={2}>
                                                    測定湿度
                                                </th>
                                                <th style={{ border: 'solid 1px', padding: 3, textAlign: 'center' }} rowSpan={2}>
                                                    最高湿度
                                                </th>
                                                <th style={{ border: 'solid 1px', padding: 3, textAlign: 'center' }} rowSpan={2}>
                                                    最低湿度
                                                </th>
                                            </>
                                        ))}
                                </tr>
                                <tbody>
                                    {Array(new Date(Number(props.temperatureLedger?.year), Number(props.temperatureLedger?.month), 0).getDate())
                                        .fill(0)
                                        .map((_, i) => {
                                            const temperatureLedgerToday = props.temperatureLedger?.monthlyTemperatureLedgers?.find(
                                                (mtl) => mtl && mtl.day === i + 1
                                            )
                                            const overTempMax =
                                                temperatureLedgerToday &&
                                                !!temperatureLedgerToday?.temperatureMax &&
                                                !!props.temperatureLedger?.controlTemperatureMax &&
                                                temperatureLedgerToday.temperatureMax > props.temperatureLedger.controlTemperatureMax

                                            const overTempMin =
                                                temperatureLedgerToday &&
                                                !!temperatureLedgerToday?.temperatureMin &&
                                                !!props.temperatureLedger?.controlTemperatureMin &&
                                                temperatureLedgerToday.temperatureMin < props.temperatureLedger.controlTemperatureMin

                                            const overHumidityMax =
                                                temperatureLedgerToday &&
                                                !!temperatureLedgerToday?.humidityMax &&
                                                !!props.temperatureLedger?.controlHumidityMax &&
                                                temperatureLedgerToday.humidityMax > props.temperatureLedger.controlHumidityMax

                                            const overHumidityMin =
                                                temperatureLedgerToday &&
                                                !!temperatureLedgerToday?.humidityMin &&
                                                !!props.temperatureLedger?.controlHumidityMin &&
                                                temperatureLedgerToday.humidityMin < props.temperatureLedger.controlHumidityMin
                                            return (
                                                <tr key={i}>
                                                    <CustomTd
                                                        style={{
                                                            border: 'solid 1px',
                                                            padding: 4,
                                                            textAlign: 'center',
                                                            backgroundColor: dayColor(
                                                                Number(props.temperatureLedger?.year ?? 0),
                                                                Number(props.temperatureLedger?.month ?? 0),
                                                                i + 1
                                                            ),
                                                        }}
                                                    >{`${i + 1}日`}</CustomTd>
                                                    <CustomTd style={{ border: 'solid 1px', padding: 3, textAlign: 'center' }}>
                                                        {temperatureLedgerToday?.time?.slice(0, 5)}
                                                    </CustomTd>
                                                    <CustomTd style={{ border: 'solid 1px', padding: 3, textAlign: 'center' }}>
                                                        {temperatureLedgerToday?.temperatureAtMeasurement}
                                                    </CustomTd>
                                                    <CustomTd
                                                        style={{
                                                            border: 'solid 1px',
                                                            padding: 3,
                                                            textAlign: 'center',
                                                            backgroundColor: !temperatureLedgerToday?.memo && overTempMax ? '#f003' : '#fff',
                                                        }}
                                                    >
                                                        {temperatureLedgerToday?.temperatureMax}
                                                    </CustomTd>
                                                    <CustomTd
                                                        style={{
                                                            border: 'solid 1px',
                                                            padding: 3,
                                                            textAlign: 'center',
                                                            backgroundColor: !temperatureLedgerToday?.memo && overTempMin ? '#f003' : '#fff',
                                                        }}
                                                    >
                                                        {temperatureLedgerToday?.temperatureMin}
                                                    </CustomTd>

                                                    {!props.temperatureLedger?.equipment?.ondotoriSerialNumber ||
                                                        (props.temperatureLedger?.equipment?.ondotoriSeries === 'TR7' && (
                                                            <>
                                                                <CustomTd style={{ border: 'solid 1px', padding: 3, textAlign: 'center' }}>
                                                                    {temperatureLedgerToday?.humidityAtMeasurement}
                                                                </CustomTd>
                                                                <CustomTd
                                                                    style={{
                                                                        border: 'solid 1px',
                                                                        padding: 3,
                                                                        textAlign: 'center',
                                                                        backgroundColor: !temperatureLedgerToday?.memo && overHumidityMax ? '#f003' : '#fff',
                                                                    }}
                                                                >
                                                                    {temperatureLedgerToday?.humidityMax}
                                                                </CustomTd>
                                                                <CustomTd
                                                                    style={{
                                                                        border: 'solid 1px',
                                                                        padding: 3,
                                                                        textAlign: 'center',
                                                                        backgroundColor: !temperatureLedgerToday?.memo && overHumidityMin ? '#f003' : '#fff',
                                                                    }}
                                                                >
                                                                    {temperatureLedgerToday?.humidityMin}
                                                                </CustomTd>
                                                            </>
                                                        ))}
                                                    <CustomTd
                                                        style={{
                                                            border: 'solid 1px',
                                                            padding: 3,
                                                            textAlign: 'center',
                                                            backgroundColor: temperatureLedgerToday && !temperatureLedgerToday?.user?.id ? '#f003' : '#fff',
                                                        }}
                                                    >
                                                        {temperatureLedgerToday && !temperatureLedgerToday?.user?.id
                                                            ? '未確認'
                                                            : temperatureLedgerToday?.user?.fullName}
                                                    </CustomTd>
                                                    <CustomTd
                                                        style={{
                                                            border: 'solid 1px',
                                                            padding: 3,
                                                            paddingLeft: 10,
                                                            backgroundColor:
                                                                !temperatureLedgerToday?.memo &&
                                                                (overTempMax || overTempMin || overHumidityMax || overHumidityMin)
                                                                    ? '#f003'
                                                                    : '#fff',
                                                        }}
                                                    >
                                                        {!temperatureLedgerToday?.memo && (overTempMax || overTempMin)
                                                            ? '温度逸脱：特記事項を入力してください'
                                                            : temperatureLedgerToday?.memo}
                                                    </CustomTd>
                                                </tr>
                                            )
                                        })}
                                </tbody>
                            </table>
                            {/* <table style={{ marginTop: 10, borderCollapse: 'collapse' }}>
                                <tr>
                                    <th style={{ padding: 3 }} colSpan={2}>
                                        定期保守点検記録
                                    </th>
                                    <th style={{ padding: 3, width: 80, paddingLeft: 300 }} colSpan={2}>
                                        {`保守点検業者名: ${props.temperatureLedger.maintenanceCompany ?? ''}`}
                                    </th>
                                </tr>
                                <tbody style={{ border: 'solid 2px' }}>
                                    <tr>
                                        <th style={{ width: 87, border: 'solid 1px', padding: 3, textAlign: 'center' }}>点検日</th>
                                        <th style={{ width: 88, border: 'solid 1px', padding: 3, textAlign: 'center' }}>担当者</th>
                                        <th style={{ width: 437, border: 'solid 1px', padding: 3 }}>作業内容</th>
                                        <th style={{ width: 138, border: 'solid 1px', padding: 3 }}>申し送り事項</th>
                                    </tr>
                                    {props.temperatureLedger.inspectTemperatureLedgers?.length === 0 ? (
                                        <tr>
                                            <CustomTd style={{ padding: 10, border: 'solid 1px' }}></CustomTd>
                                            <CustomTd style={{ padding: 10, border: 'solid 1px' }}></CustomTd>
                                            <CustomTd style={{ padding: 10, border: 'solid 1px' }}></CustomTd>
                                            <CustomTd style={{ padding: 10, border: 'solid 1px' }}></CustomTd>
                                        </tr>
                                    ) : (
                                        props.temperatureLedger.inspectTemperatureLedgers?.map((c, i) => (
                                            <tr key={i} style={{ height: 22 }}>
                                                <CustomTd style={{ border: 'solid 1px', padding: 3, textAlign: 'center' }}>
                                                    {props.temperatureLedger?.inspectTemperatureLedgers === undefined ? undefined : c?.date}
                                                </CustomTd>
                                                <CustomTd style={{ border: 'solid 1px', padding: 3, textAlign: 'center' }}>
                                                    {props.temperatureLedger?.inspectTemperatureLedgers === undefined ? undefined : c?.user?.fullName}
                                                </CustomTd>
                                                <CustomTd style={{ border: 'solid 1px', padding: 3 }}>
                                                    {props.temperatureLedger?.inspectTemperatureLedgers === undefined ? undefined : c?.content}
                                                </CustomTd>
                                                <CustomTd style={{ border: 'solid 1px', padding: 3 }}>
                                                    {props.temperatureLedger?.inspectTemperatureLedgers === undefined ? undefined : c?.message}
                                                </CustomTd>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table> */}
                        </div>
                    </div>
                </A4>
            ) : (
                <div>
                    <div style={{ marginTop: 20, padding: '100px 0', textAlign: 'center', backgroundColor: 'white' }}>当月の台帳を作成してください</div>
                </div>
            )}
        </>
    )
}
